// app/components/Plugins/Automations/nodes/implementations/StatusNodeImpl.ts

import { BaseNode } from '../../core/BaseNode';
import { ExecutionContext } from '../../core/protocols';
import { AutomationNode, StatusNodeData } from '../../types';

/**
 * Implementation of the Status Node protocol
 * Responsible for tracking and reporting the status of automation flows
 */
export class StatusNodeImpl extends BaseNode {
  constructor(node: AutomationNode) {
    super(node);
  }

  /**
   * Process the status node
   * Updates and reports the current status of the automation flow
   */
  async process(context: ExecutionContext): Promise<boolean> {
    // Get the last order information from context if available
    const lastOrder = context.data.lastOrder;
    const conditionMet = context.data.conditionMet;
    
    // Update the status in node data
    const statusData = this.data as StatusNodeData;
    statusData.status = 'COMPLETED';
    
    // Log the status update
    context.addLog('Automation flow completed successfully');
    
    // If we have order information, log it
    if (lastOrder) {
      context.addLog(`Last action: ${lastOrder.action} ${lastOrder.quantity} shares of ${lastOrder.ticker}`);
      context.addLog(`Execution price: $${lastOrder.price.toFixed(2)}`);
      context.addLog(`Total value: $${lastOrder.value.toFixed(2)}`);
    }
    
    // If we have condition information, log it
    if (conditionMet !== undefined) {
      context.addLog(`Condition evaluation: ${conditionMet ? 'Condition met' : 'Condition not met'}`);
    }
    
    // Store the completion timestamp
    context.data.completionTime = new Date().toISOString();
    
    // Status nodes always return true to indicate successful processing
    return true;
  }

  /**
   * Validate the status node configuration
   * Status nodes don't have specific configuration requirements
   */
  validate() {
    // Use the base validation
    return super.validate();
  }

  /**
   * Get the input ports for this node
   * Status node has one input
   */
  getInputs(): string[] {
    return ['in'];
  }

  /**
   * Get the output ports for this node
   * Status node has no outputs as it's typically an end node
   */
  getOutputs(): string[] {
    return [];
  }
}